import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import {
  ErrorTooltip,
  type Field,
  type Patient,
  Text,
} from '@pbt/pbt-ui-components'

import type { Contact } from '~/api/graphql/generated/types'
import {
  ConsentStatusName,
  PriorVetCareStatusName,
} from '~/constants/previousMedicalRecord'
import i18nPortal from '~/locales/i18n'
import {
  getConsentStatus,
  getPriorVetCareStatus,
} from '~/store/reducers/constants'

import PreviousPracticesForm from './PreviousPracticesForm'

const useStyles = makeStyles(
  (theme) => ({
    sectionHeading: {
      fontSize: '1.4rem',
    },
    radioGroup: {
      '& .MuiFormControlLabel-root': {
        marginBottom: theme.spacing(-1),
        '&:last-child': {
          marginBottom: 0,
        },
      },
    },
    hasSeenVetRadioGroup: {
      width: '70%',
    },
    petParentConsentRadioGroup: {
      width: '40%',
    },
    sectionContainer: {
      width: 'fit-content',
    },
    errorTooltipWrapper: {
      marginTop: `${theme.spacing(-2)} !important`,
    },
    errorTooltip: {
      width: 'fit-content',
      maxWidth: 'none',
    },
    errorText: {
      color: theme.palette.error.main,
    },
    petParentConsentBox: {
      backgroundColor: theme.colors.shadesNeedsAttention,
      border: `1px solid ${theme.colors.uiNeedsAttention}`,
      borderRadius: 2,
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    petParentConsentText: {
      color: theme.colors.uiNeedsAttention,
    },
  }),
  { name: 'PMRConsentStep' },
)

const LABEL_BY_PRIOR_VET_NAME: Record<string, string> = {
  [PriorVetCareStatusName.YES]: i18nPortal.t('Common:YES'),
  [PriorVetCareStatusName.NO]: i18nPortal.t('Common:NO'),
  [PriorVetCareStatusName.UNKNOWN]: i18nPortal.t('Common:UNKNOWN'),
}

// We only want to display selectable options on Rhapsody side the options below (Yes and No)
const LABEL_BY_CONSENT_NAME: Record<string, string> = {
  [ConsentStatusName.GRANTED]: i18nPortal.t('Common:YES'),
  [ConsentStatusName.DENIED]: i18nPortal.t('Common:NO'),
}

const PRIOR_VET_CARE_ORDER = Object.keys(LABEL_BY_PRIOR_VET_NAME)
const CONSENT_STATUS_ORDER = Object.keys(LABEL_BY_CONSENT_NAME)

export interface PMRConsentStepProps {
  fields: {
    hasSeenVeterinarian: Field
    petParentConsent: Field
    previousClinics: Field
  }
  headerPadding: number
  insideEditPMRDialog?: boolean
  patient?: Patient | null
}

const PMRConsentStep = ({
  fields,
  headerPadding,
  insideEditPMRDialog = false,
  patient,
}: PMRConsentStepProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'TimeTable'])
  const consentStatusConstants = useSelector(getConsentStatus)
  const priorVetCareStatusConstants = useSelector(getPriorVetCareStatus)

  const sortedPriorVetCareConstants = [...priorVetCareStatusConstants].sort(
    (a, b) => {
      const indexA = PRIOR_VET_CARE_ORDER.indexOf(a.name)
      const indexB = PRIOR_VET_CARE_ORDER.indexOf(b.name)
      return (
        (indexA === -1 ? Infinity : indexA) -
        (indexB === -1 ? Infinity : indexB)
      )
    },
  )
  const sortedConsentConstants = [...consentStatusConstants].sort((a, b) => {
    const indexA = CONSENT_STATUS_ORDER.indexOf(a.name)
    const indexB = CONSENT_STATUS_ORDER.indexOf(b.name)
    return (
      (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
    )
  })

  const { hasSeenVeterinarian, petParentConsent, previousClinics } = fields

  const priorVetCareYesId = priorVetCareStatusConstants.find(
    (c) => c.name === PriorVetCareStatusName.YES,
  )?.id

  const showFindPreviousPractice =
    hasSeenVeterinarian.value === priorVetCareYesId

  const contactsList = previousClinics.value as Contact[]
  const showConsentSection = contactsList.length > 0 && showFindPreviousPractice

  return (
    <>
      {!insideEditPMRDialog && (
        <Text variant="h2">
          {t('TimeTable:ADD_APPOINTMENT_COMPONENT.NEW_APPOINTMENT')}
        </Text>
      )}
      <Grid
        container
        className={classes.sectionContainer}
        direction="column"
        mb={1}
        mt={headerPadding}
      >
        {!insideEditPMRDialog && (
          <Text className={classes.sectionHeading} pb={1} variant="h2">
            {t('Common:PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
          </Text>
        )}
        <Text
          className={
            hasSeenVeterinarian.open && !hasSeenVeterinarian.valid
              ? classes.errorText
              : undefined
          }
          pb={0}
          variant="body2"
        >
          {patient?.name
            ? t('TimeTable:PMR_CONSENT.HAS_SEEN_VETERINARIAN_QUESTION', {
                patientName: patient.name,
              })
            : t(
                'TimeTable:PMR_CONSENT.HAS_SEEN_VETERINARIAN_QUESTION_FALLBACK',
              )}
        </Text>
        <ErrorTooltip
          classes={{
            errorPopper: classes.errorTooltipWrapper,
            errorTooltip: classes.errorTooltip,
          }}
          message={hasSeenVeterinarian.message}
          open={hasSeenVeterinarian.open && !hasSeenVeterinarian.valid}
          placement="bottom-start"
        >
          <RadioGroup
            aria-label="has-seen-veterinarian"
            className={classNames(
              classes.radioGroup,
              classes.hasSeenVetRadioGroup,
            )}
            name="hasSeenVeterinarian"
            value={hasSeenVeterinarian.value}
            onChange={(e) => hasSeenVeterinarian.setValue(e.target.value)}
          >
            {sortedPriorVetCareConstants.map((c) => {
              const displayedValue = LABEL_BY_PRIOR_VET_NAME[c.name]

              if (!displayedValue) {
                return null
              }

              return (
                <FormControlLabel
                  control={<Radio />}
                  key={c.id}
                  label={displayedValue}
                  value={c.id}
                />
              )
            })}
          </RadioGroup>
        </ErrorTooltip>
      </Grid>
      {showFindPreviousPractice && (
        <PreviousPracticesForm
          insideEditPMRDialog={insideEditPMRDialog}
          previousClinics={previousClinics}
        />
      )}
      {showConsentSection && (
        <Grid
          container
          className={classes.petParentConsentBox}
          direction="column"
          mb={2}
        >
          <Text
            className={
              petParentConsent.open && !petParentConsent.valid
                ? classes.errorText
                : classes.petParentConsentText
            }
            variant="h4"
          >
            {t('TimeTable:PMR_CONSENT.PET_PARENT_CONSENT_TITLE')}
          </Text>
          <Text
            className={
              petParentConsent.open && !petParentConsent.valid
                ? classes.errorText
                : classes.petParentConsentText
            }
            pb={1}
            variant="body2"
          >
            {t('TimeTable:PMR_CONSENT.PET_PARENT_CONSENT_QUESTION')}
          </Text>
          <ErrorTooltip
            classes={{
              errorPopper: classes.errorTooltipWrapper,
              errorTooltip: classes.errorTooltip,
            }}
            message={petParentConsent.message}
            open={petParentConsent.open && !petParentConsent.valid}
            placement="bottom"
          >
            <RadioGroup
              aria-label="pet-parent-consent"
              className={classNames(
                classes.radioGroup,
                classes.petParentConsentRadioGroup,
              )}
              name="petParentConsent"
              value={petParentConsent.value}
              onChange={(e) => petParentConsent.setValue(e.target.value)}
            >
              {sortedConsentConstants.map((c) => {
                const displayedValue = LABEL_BY_CONSENT_NAME[c.name]

                if (!displayedValue) {
                  return null
                }

                return (
                  <FormControlLabel
                    control={<Radio />}
                    key={c.id}
                    label={displayedValue}
                    value={c.id}
                  />
                )
              })}
            </RadioGroup>
          </ErrorTooltip>
        </Grid>
      )}
    </>
  )
}

export default PMRConsentStep
